// Phase 26 — shared task row for CaseView and CollectiveCaseView.
//
// Replaces the two near-duplicate inline <tr> blocks. Renders one task row plus
// (when expanded) its SubtaskChecklist row. Behaviour is identical in both
// views; per-view wiring (hard-delete semantics) is passed in as props.
//
// Deliverable A (parity) — this single component levels the previous CV/CCV
// drift up to the fuller (CaseView) behaviour everywhere:
//   * subtask counter column + expandable SubtaskChecklist row,
//   * notes / waitingReason / delegated "waiting for" / docLink / "Resume",
//   * archived styling (line-through + italic) and the non-owner Archive action,
//   * §4.48 actor-based (createdBy) edit/delete gate with confirmAndUndo delete.
//
// Deliverable B (inline edit) — in the UNLOCKED state, and only for tasks the
// actor may edit, name / date / Level (priority) / Eisenhower (caseEisenhower)
// become click-to-edit using the same control vocabulary as the canonical "tk"
// modal. Edits persist through the same write path the modal uses (svTkData).
// The Eisenhower picker marks the task's current quadrant ("you are here"). The
// Edit pencil is retained for add + full edit.

/* 216 addendum 4 — `import React` for `react/react-in-jsx-scope`, the rule's own
   remedy (166's reading, AgendaView.tsx): the ratchet counts the rule once per
   JSX element, §8 (4) adds two to this file, and putting React in scope clears
   the same debt for every element already here. Nothing at runtime changes. */
import React from "react";
import { Fragment, useState, useEffect } from "react";
import { createPortal } from "react-dom";
/* BRIEF 105 §3a — `FileText` BECOMES `File`, ON EVERY ROW-LEVEL DOCUMENT
   AFFORDANCE. The brief named four coordinates and said "check for others";
   the census over `src/` found SIX of this class and two the brief had not:
     CaseEventRow.tsx:483   the event's documents drawer toggle
     CaseEventRow.tsx:563   the event's docLink anchor
     CaseTaskRow.tsx:1105   this file's documents drawer toggle
     Dashboard.tsx:1260     the Dashboard event card's docLink anchor
     Dashboard.tsx:2226     the Dashboard TASK row's drawer toggle  ← not named
     AgendaView.tsx:1051    the agenda row's document-open button   ← not named
   THE BOUND IS THE ROW, and it is stated rather than widened: 19 other
   `FileText` sites live in the document library, the generators, the billing
   modals, the timeline and a taxonomy icon NAME stored as a string
   (`utils.ts:785`, `taxonomyMap.ts:290`) — different surfaces, none of them
   glanced here, and the last of those is data rather than an import. A sweep
   of those is its own census. */
import { Pencil, Archive, Play, X, Clock, File } from "lucide-react";
import { isDividerCode } from "../utils/taxonomyMap"; // 110 §3a
import { useTaxonomy } from "../hooks/useTaxonomy"; // 136 §2
import { isArchivableStatus } from "../lib/taskCounters"; // 136 §2
import {
  I,
  fmtElapsed,
  pname,
  coIni,
  eisenQuadrant,
  EISEN_QUADRANTS,
  provisionalLevel,
} from "../utils";
import { EisenBadge, eisenLabel } from "../EisenBadge";
import { useTranslation } from "../i18n";
import { isArchivedCo } from "../lib/collaboratorState";
import { ArchivedHoleBadge } from "./SharedUI";
import { InitialsBadge } from "./SharedUI";
import SubtaskChecklist from "./SubtaskChecklist";
import TaskDocsPane from "./TaskDocsPane";
import BadgeHoverArea from "./BadgeHoverArea"; // 117 §3
// BRIEF 95 §3a(a) — one date control, DD/MM/YY at rest and ISO on the wire.
// BRIEF 100 §2 — `dmy2` left this file with the flat due cell; the helper is
// untouched (§2b) and still serves `SubtaskChecklist`'s date column.
import DateField from "./DateField";
// BRIEF 101 §1 — the date block, extracted; this file was one of its three copies.
import DateBlock from "./DateBlock";
// BRIEF 105 §3b — the third drawer.
import NotesDrawer, { hasNoteText } from "./NotesDrawer"; // 118 §9
// BRIEF 107 §2b — the two icons that open a drawer, and the one definition of
// "this row has something" they both render through.
import {
  NotesToggle,
  DocsToggle,
  docsToggleVisible,
} from "./RowDrawerToggle";
import LinkIcon from "./LinkIcon"; // 111 §5c
import SelfTick from "./SelfTick";
import { RowTimerBlock } from "./RowTimerBlock"; // 116 §3
import AssigneePicker from "./AssigneePicker";
import MenuSelect from "./MenuSelect"; // 154B §2
import MatterChip from "./MatterChip"; // 216 addendum 6 §10 (2) — the matter pill, one definition
import { subtaskTiming } from "../lib/subtaskTiming"; // brief 91 §3a
import {
  stackCut, // brief 94 §1b
  BADGE_AREA_W, // 116 §2 — the two-slot reservation
  viewerInRow,
  badgeSections,
  type NameableCo,
} from "../lib/badgeStack";
// BRIEF 102 §2d — this file's own `tkClr`, extracted so the matrix cards can
// share the one definition instead of restating it.
import { taskUrgencyColor } from "../lib/taskUrgency";
import { CT, RAD, FS, DIMMED_ROW_OPACITY, Z } from "../lib/tokens"; // 195 §A.4 — the one dimming
import { TYPE_COL_W, TYPE_COL_W_REDUCED, TYPE_CHIP } from "./TypeTagBox"; // 118 §8; 204 §7.1 — the chip's one definition
import { usePermissions } from "../hooks/usePermissions"; // 190 §F.2 — the furniture keys

const todayISO = () => new Date().toISOString().slice(0, 10);

// BRIEF 102 §2d — `tkClr` MOVED to src/lib/taskUrgency.ts, unchanged, because
// the Eisenhower card now needs the same answer and §2d forbids a second copy.
// The name is kept local so every call site in this file reads as it did.
const tkClr = taskUrgencyColor;

export default function CaseTaskRow({
  t,
  /* Round 16 (C98) — steady accent highlight linking this row to the
     compact column's focused timer (the column computes ownership). */
  highlighted = false,
  /* Round 17 (C101) — case name shown just before the task title on the
     first row line (compact column passes it, resolved uniformly:
     viewer lists → row-attached hints). Empty/absent = no case part. */
  caseLabel = "",
  co = [],
  currentUser,
  tk = [],
  // taxonomy / helpers
  tkTypes = [],
  itemLevelList = [],
  priMap = {},
  activeTimers,
  // permission / lock
  tkLocked,
  canEditOthersTasks,
  permIsAdmin,
  isRP,
  // expansion state
  tkExpanded,
  setTkExpanded,
  // store / handlers
  TkStatusTag,
  startTimer,
  svTkData,
  sTk,
  openModal, // om / globalOm
  onDeleteTask, // (t) => void — view-specific confirmAndUndo hard delete
  /* Phase 29 Stage-3 C25/C26 — compact-column variant: renders ONLY
     [timer · type · eisenhower · subtask counter · title+description ·
     status pill], in that order (assignee / priority / due date / action
     buttons omitted), with the timer icon at double size. Pass
     tkLocked=true alongside it so the inline editors stay off. onNavTask:
     title-cell click navigates the MAIN window to the task in context
     (Dashboard row-click semantics) instead of inline-editing. */
  reduced = false,
  onNavTask,
  /* Phase 29 Stage-3 C62 (was C31) — in the reduced (compact-column)
     variant the Eisenhower badge is a JUMP with the exact title-click
     semantics (navigate the main window to the task in its CV/CCV context
     and highlight it); the per-row inline case-matrix it used to toggle is
     gone. The caller wires the same handler it gives onNavTask. */
  onEisenClick,
  /* Phase 29 Stage-3 C61 — checking a subtask done hands (task, subtask)
     back up so the host can run the canonical complete-style prompt +
     time-log offer (same dialog task completion uses). All three row hosts
     (CV / CCV / compact column) wire it. */
  onSubtaskDone,
  /* BRIEF 98 §4 — THE MATTER THIS ROW BELONGS TO, for the OneDrive button in
     the documents pane's dialog (110 §1a: the pane's own `oneDrive` binding —
     the OneDriveDocButton-on-the-add-row chain it originally fed is retired).
     §4b asked whether "only the two modals have a picker to offer" was a fact
     or an observation about wiring. It was an OBSERVATION: `openOneDrivePicker`
     mounts its own React root on a `document.body` container, so it inherits
     no modal context. What the button genuinely needs is the matter, and a row
     knows exactly one: its own; the host section passes it.
     ABSENT = NO BUTTON, which is how §4c's two callers stay as they are: the
     compact column mounts this component and does not pass it. */
  oneDriveMatter,
  // 110 §1h — the open-all/close-all {seq, open} broadcast (see the effect).
  drawersCast,
  /* BRIEF 118 §5 — does THIS TABLE show a level column at all? The host decides
     it once for the page it is rendering (any row carrying a level), because a
     column is a fact about a TABLE and not about a row: half the rows dropping
     a `<td>` would shift every column after them on those rows only. Absent =
     true, so a host that has not been taught keeps today's table. */
  showLevel = true,
  /* 111 §5c — the matter row holding itemLinks; optional (the compact column
     passes none → no icon, per the brief's surface list). */
  matterRow,
  /* BRIEF 115 §1 — the Dashboard mounts this row (B.3: one renderer), and the
     three props below are its union contribution.
     onRowNav: the whole-ROW navigate-and-flash click (the host builds the
     closure from its own _navCase + queueScrollToTk). Distinct from the
     reduced variant's onNavTask, which is a title-cell affordance; here the
     row itself is the target and every interactive control on it stops
     propagation. Absent = no handler, no cursor — CV/CCV unchanged. */
  onRowNav,
  /* 115 §1 — the 110 §1b / 112 §1b regime, as a prop: the Dashboard's drawers
     are VIEW-ONLY (subtasks read-only, docs quiet, notes quiet) while the LOOK
     stays the list drawers'. `tkLocked` cannot express this: the lock gates
     structure and deliberately NOT the note (109 §1d), so a locked row still
     offers note editing — which is exactly what the Dashboard must not. */
  drawersReadOnly = false,
  /* 115 §1 — the C60 viewer-relative due date (src/lib/dueToday.ts): a
     mine-via-subtask viewer sees HIS subtask's date, not the parent's. The
     Dashboard has rendered this since C60 and the component could not say it.
     Feeds the due cell AND the urgency colour, so a row late for the VIEWER
     reads late. undefined = t.dueDate, byte-identical for CV/CCV. */
  viewerDue,
  /* 195 §A [RULED — Laurent, 2026-09-18: "put into archive the exact same
     items as in CV/CCV (the FULL CARDS of events/Tasks), so just reuse what
     exists … make them dimmed though so nobody confuses them with active
     tasks/events"] — THE ARCHIVED MODE, widening the row's own archived look
     (`isArch`: line-through, italic) and its padlock. The row renders as in
     the matter — type, Eisenhower, assignee, counter, level, title with its
     matter, status, due — DIMMED by the one definition (DIMMED_ROW_OPACITY,
     the value this row already used for a completed row), and every control
     is INERT: no inline editor, no timer, no drawer opens, no status menu, no
     row click; the row's only two actions are the ones the host supplies
     through `archivedActions` (the Archive view's Restaurer and Supprimer
     définitivement), rendered in the actions cell. Every other prop keeps
     its meaning; a host that passes neither renders byte-identically. */
  archived = false,
  archivedActions,
  /* 209 §27 [Laurent] — THE HOURGLASS VIEW'S ROW, the reduced one extended:
     « time elapsed, activity tag, eisenhower, subtask icon, description of task
     on top, case name and number on bottom (now is reverse), note icon ». `lead`
     is what the row leads with in place of its start-timer and record-time
     icons — the column hands a TIMED row its elapsed (a click on it switches the
     timer to this row); the other rows keep the two icons. In the reduced
     variant the title now sits ABOVE the case's name and number, the note icon
     comes before the documents icon, and the status pill comes back at the end
     (addendum 6 — the column's own header says its rows carry it). */
  lead,
  /* 216 addendum 4 §8 (4) [RULED 79] — THE CASE NAME ON A SECOND ROW, AT THE
     RIGHT (Laurent, 14/10: « ADD the case name in 2nd row sticking to the right
     (left of the days counter) »). The Dashboard passes it — a matter's own
     rows and the compact column pass none and render as before. `label` is the
     matter's title, `title` its hover (the title with its ref), `onClick` opens
     the matter; without one the name is display-only (an off-team delegatee). */
  caseBelow,
}: any) {
  const { t: tr } = useTranslation();
  const below = (caseBelow ?? null) as { label: string; title?: string; onClick?: () => void } | null;
  /* 190 §F.2 (ruling 190 (4); glance round 2 [D] — RULED: ALL of them) — THE
     FURNITURE KEYS. Every cell of this row carries one registry key,
     `furniture.task.<cell>`, gated as `can(k)`: hidden firm-wide (the
     switchboard) → gone for the administrator too; not hidden → the role's
     own bit decides. The tasks table's header cells (CaseTasksSection.tsx)
     read the same keys, so a column goes header and cells together, as the
     level column already went (118 §5). ONE deviation from the bare `can(k)`
     shape: before the permissions payload lands `can()` answers false for
     every key, and a row that flashed empty at boot would be the defect —
     so the load window keeps every cell (`viewer === null`), exactly as
     `hidden()` keeps its answer through it. A furniture key gates chrome,
     never data: no route reads one.
     191 (1) — AN UNWRITTEN SWITCH MEANS SHOWN: a cell disappears only when
     someone switched its key off. The rule lives on the server, once
     (sectionRegistry.js `shownUnwritten` → fillUnwrittenDefaults()), and
     /permissions/me ships the unwritten key as an explicit `true`, so this
     `can(k)` reads the served map and carries no second copy. Hidden still
     beats everything (190 (4)); a key written false still hides.
     191 (2) — the title cell and the actions cell carry NO key: never
     switchable (the row would be unusable without them). */
  const perms = usePermissions();
  const fur = (k: string): boolean => perms.viewer === null || perms.can(k);
  const [edit, setEdit] = useState<any>(null); // null|'title'|'date'|'priority'|'eisen'
  // Eisenhower picker is rendered via a portal (the row's <td> is
  // overflow-clipped and lives inside a scroll container — see CLAUDE.md
  // "Portal pattern for dropdowns in overflow-clipped containers").
  const [eisenRect, setEisenRect] = useState<any>(null);
  useEffect(() => {
    if (edit !== "eisen") return;
    const close = () => setEdit(null);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, [edit]);

  const tkt2 = tkTypes.find((x: any) => x.code === t.type);
  /* 115 §1 — the date this row SHOWS (and colours by): the viewer-relative one
     when the host passes it, the task's own otherwise. `tkClr` reads exactly
     {status, dueDate}, so handing it the shown date keeps the title and the
     day telling one story — a delegatee's overdue subtask reads red even when
     the parent's own date is comfortable. */
  const dueShown = viewerDue !== undefined ? viewerDue : t.dueDate ?? null;
  const clr = tkClr({ status: t.status, dueDate: dueShown });
  // The ladder resolves the code, or the badge says so. There is no third
  // answer: a level the firm's ladder does not know is shown as its own code,
  // muted, never as a letter from a ladder this firm never defined. The
  // render sites below already gate on `t.priority`, so an unset level stays
  // the em-dash it has always been.
  const pri = priMap[t.priority] || provisionalLevel(t.priority);
  /* 195 §A — the Phase-28 boolean row (`archived: true`, any status) wears
     the row's own archived look, which until now only the legacy status
     reached: the padlock, the withheld timer and the hidden actions follow. */
  const isArch = t.status === "archived" || archived === true;
  const isOwn = !t.createdBy || t.createdBy === currentUser;
  const canEdit = isOwn || canEditOthersTasks;
  /* BRIEF 136 §2 [RULED] — THE ARCHIVE ICON WAITS FOR A REASON.
     The taxonomy is read HERE rather than threaded in as a prop, which is
     `SubtaskChecklist`'s own reasoning at :144 and for the same cause: this row
     mounts under CaseTasksSection, the Dashboard and the compact column, and
     threading one more prop through all three is how two surfaces come to
     answer one question differently. `useTaxonomy` is the app's own cache. */
  const { entries: taskStatusEntries } = useTaxonomy("task_status");
  const canArchive = isArchivableStatus(t.status, taskStatusEntries);
  /* 182 addition 4 [RULED 2026-09-11, Laurent] — IN UNLOCK MODE THE ARCHIVE
     ICON ALWAYS APPEARS; while the status is not terminal it is DIMMED and its
     hover says why. 136 §2's gate becomes a dimmed state, never an absence —
     otherwise how a task is removed is anyone's guess. The 181 B.2b pattern:
     the bin says what it can do (SettingsTaxonomies' refused bin — disabled,
     opacity .4, not-allowed, the sentence on hover). The gate stays the
     ENFORCING end: a dimmed icon is `disabled`, its handler returns, and the
     server's own refusal is untouched. */
  const archiveTitle = canArchive
    ? tr("common.archive", { fallback: "Archive" })
    : tr("task.archive_needs_reason", { fallback: "Set the status to completed or cancelled first, then archive" });
  const archiveDim = {
    opacity: canArchive ? 1 : 0.4,
    cursor: canArchive ? "pointer" : "not-allowed",
  } as const;
  // Inline editors share the Edit-pencil gate: unlocked, not archived, writable.
  /* 195 §A — the archived mode is the padlock AND the view-only regime at
     once; the drawers' toggles go inert below. */
  const locked = Boolean(tkLocked) || archived === true;
  const readOnly = Boolean(drawersReadOnly) || archived === true;
  const inlineOk = !locked && !isArch && canEdit;
  /* 109 §1d — notes are the OPPOSITE of structure: a thought is jotted at any
     time, no ceremony. The RIGHTS half of inlineOk without the lock — the
     padlock gates structure, never the note. Archived rows stay read-only. */
  const notesOk = !isArch && canEdit;

  const commitField = (patch: any) => {
    svTkData({ ...t, ...patch });
    setEdit(null);
  };

  // 154B §2 — the stable ref callback that focused a mounted <select> and
  // called showPicker() on it is `autoOpen` now. One click on the cell still
  // both enters edit and opens the list; the list is the app's own menu, which
  // opens from the component's state instead of by asking the DOM — so the
  // transient-user-activation question showPicker had to ask is gone with it.

  const curQuad = eisenQuadrant(t);
  const hasExplicitQuad = !!(t.caseEisenhower || t.eisenhower);

  const delTask2 = t.delegatedTaskId
    ? tk.find((x: any) => x.id === t.delegatedTaskId)
    : null;
  const revDone2 = delTask2 && delTask2.status === "completed";

  // Round 13 (C69) split full-edit from the delegatee's per-row rights;
  // round 14 (C81) RE-UNIFIES them upward: a DELEGATION-INDUCED assignee
  // (holder of ≥1 OPEN subtask, mirrored by the server-managed
  // delegatedCoIds) is treated EXACTLY like an explicit assignee for ALL
  // subtask operations — edit text/dates, tick/untick ANY row, assign,
  // archive/restore, reorder, timer/time-log on any row. The server's C81
  // gate (routes/subtasks.js) accepts the same set; accountability rides
  // the chronology. holdsSubtask (any row, open or done) still drives the
  // narrower fallbacks (docs touch, the C68a self-untick of a done row).
  const subtaskFullEdit =
    !!permIsAdmin ||
    isRP ||
    !!canEditOthersTasks ||
    (currentUser != null &&
      (currentUser === t.assignedCoId ||
        (Array.isArray(t.delegatedCoIds) &&
          t.delegatedCoIds.includes(currentUser)) ||
        (Array.isArray(t.subtasks) &&
          t.subtasks.some(
            (st: any) =>
              st && !st.done && !st.archived && st.assignedCoId === currentUser,
          ))));
  const holdsSubtask =
    currentUser != null &&
    Array.isArray(t.subtasks) &&
    t.subtasks.some((st: any) => st && st.assignedCoId === currentUser);
  // C72 — document rights: any assignee, explicit or delegation-induced,
  // plus the usual edit grants (mirrors the server's task-docs gate).
  const docsCanTouch =
    subtaskFullEdit ||
    holdsSubtask ||
    (currentUser != null &&
      Array.isArray(t.delegatedCoIds) &&
      t.delegatedCoIds.includes(currentUser));
  // C75 — archived subtasks leave every counter; C72 — archived docs don't
  // count toward the badge (the pane still lists them, dimmed).
  const liveSubs = Array.isArray(t.subtasks)
    ? t.subtasks.filter((st: any) => st && !st.archived)
    : [];
  const liveDocs = Array.isArray(t.docs)
    ? t.docs.filter((d: any) => d && !d.archived)
    : [];
  /* BRIEF 117 §7 — "the chip renders only when the drawer it opens can be
     added to", written down ONCE per drawer instead of restated at each
     affordance. Each is the drawer's OWN add predicate, copied from where the
     drawer enforces it: subtasks `writable && !locked && !readOnly`
     (SubtaskChecklist.tsx:662, 116 §1) and documents `canWrite && !locked`
     (TaskDocsPane.tsx:364, this brief) — the pane folds `readOnly` into
     `canWrite` at the mount below (`drawersReadOnly ? false : docsCanTouch`),
     so it is spelled out here.
     THE `readOnly` TERM ON THE SUBTASK CHIP IS 115's PENDING ALIGNMENT, taken.
     115 §2 withheld the event card's empty-state "+" under the view-only regime
     and left the task side's alone ("each pane keeps its history; one ruling
     could align them"). §7 is that ruling, and it is about what the affordance
     OPENS, not about which surface it is on. */
  const subtaskCanAdd = subtaskFullEdit && !locked && !readOnly;
  const docsCanAdd = docsCanTouch && !locked && !readOnly;
  const [docsOpen, setDocsOpen] = useState(false);
  /* BRIEF 105 §3b — THE THIRD DRAWER. Local state, exactly like `docsOpen`
     beside it: it is a per-row disclosure and no host asks about it.
     §3b-ii's gate is `inlineOk` — the SAME gate the title, the date, the type,
     the level and the assignee on this row already use, because all six write
     the same row through the same route. A notes field editable under different
     conditions from the title, on one row, would be the invention §3b-ii warns
     about. (`tasks.js:330` accepts `caseView.tasks.edit` and lets an explicit
     assignee change `status` ALONE; `inlineOk`'s `isOwn` term is wider than
     that for every one of the six, which is a pre-existing disagreement this
     brief inherits rather than creates — recorded in the PR.) */
  const [notesOpen, setNotesOpen] = useState(false);
  /* 110 §1h — the host's open-all/close-all broadcast: a {seq, open} tick the
     row SUBSCRIBES to (no remount, no lifted state). Runs on mount too, so a
     row paged in during a live sweep adopts it. The subtasks drawer is host
     state (tkExpanded) and the host sets it directly. */
  useEffect(() => {
    if (!drawersCast || !drawersCast.seq) return;
    /* 111 §2 — an OPEN tick opens only a drawer that HAS something, using the
       icons' own facts (DocsToggle's liveDocs, NotesToggle's !!t.notes,
       :1108/:1122). A CLOSE tick still closes everything. */
    setDocsOpen(!!drawersCast.open && liveDocs.length > 0);
    setNotesOpen(!!drawersCast.open && hasNoteText(t.notes)); // 118 §9
  }, [drawersCast]);
  /* BRIEF 91 §3a — the five timer props are ONE value now, built by
     `subtaskTiming` (src/lib/subtaskTiming.ts). The four derivations that stood
     here — `isTimerRunning`, `runningSubtaskId` and Round 15 (C92)'s
     `subtaskTimers`, plus the `te` seed inside `onLogTime` — existed ONLY in
     this file, which is why the event wrapper never had them: there was nothing
     to import. They are unchanged in behaviour and are now importable, and the
     argument list is required, so a host that was not handed `activeTimers` /
     `startTimer` / `openModal` fails the typechecker instead of rendering a row
     with a missing button. */
  const timing = subtaskTiming({
    kind: "task",
    parentId: t.id,
    caseId: t.caseId ?? null,
    ccId: t.ccId ?? null,
    type: t.type ?? null,
    currentUser: currentUser ?? null,
    activeTimers,
    startTimer,
    openModal,
  });

  /* Timer cell — extracted to a const because the reduced (compact-column)
     variant renders it FIRST in the row while the full variant keeps its
     historical slot after Assigned. C10: double-size icon when reduced.
     Round 13 (C80) gave the full (CV/CCV) variant a time-log watch icon
     immediately AFTER the timer triangle (same prefill conventions as the
     subtask log buttons: viewer logs, task title as description); round 14
     (C83iv) extends it to the reduced (pop-out/column) variant — the
     openModal the column passes is omMain, so the te modal opens in the
     MAIN window with the focus pull. */
  /* 116 §3 — THE BLOCK ITSELF IS `RowTimerBlock` NOW, and this row is still its
     master: the twelve style properties, the C92 running/paused states and the
     115 §1 stopPropagation moved into that file UNCHANGED (measured before and
     after: block 39 · play 20 · gap 1 · clock 18 · ink 8 · icons 12/12). What
     stays here is what is genuinely this row's — the `<td>`, its `reduced`
     doubling, the "is there work to time" predicate, and the two closures.
     §3 gives the same block to an agenda event; copying it there would have
     made a third spelling of one control, which is the defect 115 §4 ruled on
     (a shared control's geometry is measured from its master, not invented). */
  /* 209 §27 — the reduced row stacks the title above its case; elsewhere the
     case precedes the title on one line (Round 17 C101, 195 §A). */
  const caseLineLayout = reduced
    ? { flexDirection: "column" as const }
    : { alignItems: "baseline", gap: 4 };
  // the drawers' span: the reduced row has 6 cells since 209 §27 (the status pill)
  let drawerSpan = showLevel ? 8 : 7;
  if (reduced) drawerSpan = 6;
  /* 216 addendum 7 §11 (2) [RULED 84] — THE CASE PILL ON THE SECOND ROW, IN THE STATUS PILL'S COLUMN, NEXT TO THE
     DUE DATE (Laurent, 16/10: « put case name in 2nd row, NEXT to the counter (like the status pill of the main
     task above it) »): the second row's first cell spans every column before the status pill's (the cells this
     row renders, gate for gate), the pill sits in the status column, and the due date and the actions cell of the
     row above span BOTH rows, so the date block centres between them (`.tkRow > td` is vertical-align middle).
     Only where the row carries a status cell and is not the reduced one; else the pill keeps addendum 6's place,
     at the right end of a full-width cell. */
  const pillRow = !reduced && !!(below && below.label) && fur("furniture.task.status");
  const cellsBeforeStatus = [
    fur("furniture.task.type"),
    fur("furniture.task.eisenhower"),
    fur("furniture.task.assignees"),
    fur("furniture.task.timer"),
    fur("furniture.task.subtasks"),
    showLevel && fur("furniture.task.level"),
    true, // the title
  ].filter(Boolean).length;
  /* 209 §27 — the documents icon, written once: the reduced row shows it after
     the note icon, every other row before it. */
  const docsToggle = docsToggleVisible(liveDocs, docsCanAdd) && (
    <DocsToggle
      open={docsOpen}
      hasDocs={liveDocs.length > 0}
      onToggle={archived ? undefined : () => setDocsOpen((v) => !v)} /* 195 §A — inert */
    />
  );
  const tdTimer = fur("furniture.task.timer") && (
    <td style={{ padding: "6px 2px", textAlign: "center", whiteSpace: "nowrap" }}>
      {lead !== undefined && lead /* 209 §27 — a timed row's elapsed */}
      {lead === undefined && t.status !== "completed" && !isArch && (
        <RowTimerBlock
          /* Round 15 (C92) — task-level display matches task-level timers ONLY:
             the find used to match ANY timer with this taskId, so a SUBTASK
             timer hijacked the main row's readout and the main button's
             startTimer(t.id) keyed (taskId, null), which could never resume the
             pair it appeared to show. */
          timer={(activeTimers || []).find(
            (x: any) => x.taskId === t.id && !x.subtaskId,
          )}
          reduced={reduced}
          onStart={() => startTimer(t.id)}
          logTitle={tr("task.log_time_tip", {
            fallback: "Add a time entry on this task",
          })}
          onLog={() =>
            openModal("te", {
              caId: t.caseId || "",
              ccId: t.ccId || "",
              coId: currentUser || "",
              date: todayISO(),
              type: t.type || "",
              desc: t.title || "",
              billed: false,
              min: 0,
              _fromTask: t.id,
            })
          }
        />
      )}
    </td>
  );

  return (
    <Fragment>
      <tr
        id={`tk-${t.id}`}
        data-lnk={`task-${t.id}`}
        className={"tkRow" + (highlighted ? " colTHl" : "")}
        /* 115 §1 — the Dashboard's row-click semantics, on the row. Only the
           main <tr> carries it: the drawer rows below are separate <tr>s, so a
           click inside an open drawer navigates nowhere by construction. */
        onClick={onRowNav && !archived ? () => onRowNav(t) : undefined}
        style={{
          opacity: t.status === "completed" || isArch ? DIMMED_ROW_OPACITY : 1, // 195 §A.4 — the one dimming
          ...(onRowNav && !archived ? { cursor: "pointer" } : {}),
          /* 195 §A — the stylesheet's `tbody tr { cursor: pointer }` (App.css)
             would promise a click on an inert row; the cells inherit this. */
          ...(archived ? { cursor: "default" } : {}),
        }}
      >
        {/* C25 — reduced order leads with the (double-size) timer cell. */}
        {reduced && tdTimer}
        {/* Type — inline editable (kept within its own narrow column so the
            select can't overlay the Eisenhower / timer controls to its right;
            the native option list is OS-rendered and stays fully readable).
            C30 — reduced variant: a touch bigger and centered between the
            timer triangle and the Eisenhower indicator. */}
        {/* BRIEF 118 §8 — the two literals become the constant the event cards
            cite. 38 → `TYPE_COL_W` (60) is a no-op on this table: MEASURED, the
            column already rendered 60 under the header's own content. 52 is
            C30's compact number, unchanged. */}
        {fur("furniture.task.type") && (
        <td
          style={
            reduced
              ? { width: TYPE_COL_W_REDUCED, textAlign: "center" }
              : { width: TYPE_COL_W }
          }
        >
          {edit === "type" ? (
            <MenuSelect
              className="field-input"
              style={{ width: "100%", margin: 0, fontSize: FS.s10_5, padding: "1px 2px" }}
              autoOpen
              value={t.type || ""}
              onChange={(v) => commitField({ type: v || null })}
              onBlur={() => setEdit(null)}
              options={[
                ["", tr("common.select_dashed", { fallback: "— Select —" })] as const,
                /* 110 §3a's divider is 108 §6b's row now — the branch this
                   site once had to be taught is the menu's own question. */
                ...tkTypes.map(
                  (ty: any) =>
                    [
                      String(ty.code),
                      isDividerCode(ty.code)
                        ? String(ty.desc || "")
                        : `${ty.code} — ${ty.desc}`,
                    ] as const,
                ),
              ]}
            />
          ) : tkt2 ? (
            <span
              /* 157A §8 RULED (2026-08-31, DIRECTION CORRECTED) — THE MODEL IS
                 THE TIMER CHIP, and this is one of the two that drifted from
                 it. The popout is THREE components, not one: the Minuteur
                 section is CompactColumn's own row, but "Tâches du jour"
                 mounts THIS component with `reduced` (CompactColumn.tsx:560)
                 and "Événements du jour" mounts CaseEventRow with `reduced`
                 (CompactColumn.tsx:660). An earlier pass
                 read only CompactColumn.tsx, called that the popout, and
                 "reconciled" the one chip that was already right — dragging
                 the model down to `.tag` instead of bringing the strays up to
                 it. That is reverted; `.colTag` is the standard again.
                 SCOPED TO `reduced`, which is what the popout sets. The main
                 window's task tables keep the chip they have always had — the
                 ruling is about the popout's three sections agreeing with each
                 other, and restyling a main-window surface was never asked
                 for and would be the same overreach in the other direction.
                 `.colTag` supplies size, weight, padding, radius, colour,
                 background and the monospace face, so nothing here restates
                 them; only the cursor, which is behaviour and depends on
                 whether this row's type is editable. */
              className={reduced ? "colTag" : undefined}
              title={inlineOk ? "Click to change type" : tkt2.desc}
              onClick={inlineOk ? () => setEdit("type") : undefined}
              style={
                reduced
                  ? { cursor: inlineOk ? "pointer" : "default" }
                  : {
                      /* 151E §2(a) — the column rows return to NORMAL size:
                         the reduced variant's deliberate enlargements (the
                         C10-era double-size touch targets) are ruled out;
                         `reduced` stays a LAYOUT variant only.
                         204 §7.1 — the shape is the task-type chip's ONE
                         definition (TypeTagBox's TYPE_CHIP, 204 §C.0), no
                         longer this site's own copy (it read 10.5 / 1px 5px /
                         r4); only the cursor is this cell's. */
                      ...TYPE_CHIP,
                      cursor: inlineOk ? "pointer" : undefined,
                    }
              }
            >
              {t.type}
            </span>
          ) : (
            <span
              onClick={inlineOk ? () => setEdit("type") : undefined}
              style={{ color: "var(--tm)", cursor: inlineOk ? "pointer" : undefined }}
              title={inlineOk ? "Click to set type" : undefined}
            >
              —
            </span>
          )}
        </td>
        )}
        {/* Eisenhower — inline picker (full variant) or, when reduced, a
            jump to the task in its CV/CCV context, exactly like the title
            click (C62). */}
        {fur("furniture.task.eisenhower") && (
        <td
          style={{ width: 22, textAlign: "center", padding: "6px 2px" }}
          title={eisenLabel(curQuad)}
        >
          <span
            onClick={
              reduced && onEisenClick
                ? () => onEisenClick(t)
                : inlineOk
                  ? (e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setEisenRect({ left: r.left, top: r.bottom });
                      setEdit("eisen");
                    }
                  : undefined
            }
            title={
              reduced && onEisenClick
                ? tr("col.open_in_case_tip", { fallback: "Open in case" })
                : undefined
            }
            style={{
              cursor:
                (reduced && onEisenClick) || inlineOk ? "pointer" : undefined,
              display: "inline-flex",
            }}
          >
            <EisenBadge q={curQuad} size={12} />
          </span>
          {edit === "eisen" &&
            eisenRect &&
            createPortal(
              <>
                <div
                  onClick={() => setEdit(null)}
                  style={{ position: "fixed", inset: 0, zIndex: Z.paneBackdrop }}
                />
                <div
                  style={{
                    position: "fixed",
                    top: eisenRect.top + 4,
                    left: Math.max(
                      8,
                      Math.min(eisenRect.left, window.innerWidth - 280),
                    ),
                    background: "var(--bc)",
                    border: "1px solid var(--br)",
                    borderRadius: RAD.r8,
                    padding: 6,
                    boxShadow: "0 6px 20px rgba(0,0,0,.6)",
                    zIndex: Z.pane,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    minWidth: 260,
                    textAlign: "left",
                  }}
                >
                  {EISEN_QUADRANTS.map((q) => {
                    const here = q.k === curQuad;
                    return (
                      <div
                        key={q.k}
                        style={{
                          padding: "8px 10px",
                          border:
                            (here ? "2px solid " : "1px solid ") + q.border,
                          borderRadius: RAD.r6,
                          background: q.bg,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: FS.s10_5,
                          color: q.accent,
                          fontWeight: 600,
                        }}
                        onClick={() => commitField({ caseEisenhower: q.k })}
                      >
                        <EisenBadge q={q.k} size={16} />
                        <span>{eisenLabel(q.k)}</span>
                        {here && (
                          <span style={{ marginLeft: "auto", fontSize: FS.s11 }}>
                            {hasExplicitQuad
                              ? "✓"
                              : tr("common.auto_paren", { fallback: "(auto)" })}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>,
              document.body,
            )}
        </td>
        )}
        {/* Assigned — inline editable (protected; same inlineOk gate, same
            control + self-only-for-non-admin filter as the canonical modal).
            Omitted in the reduced (compact-column) variant. */}
        {!reduced && fur("furniture.task.assignees") && (
        <td
          /* 116 §2 — 28 was a DECLARATION the content overruled: measured on
             watt's 2026/001, a six-badge row drew 102px of ink here and the
             table widened this column to 106 for every row in it. With the
             stack capped at two the reservation can finally be honoured —
             BADGE_AREA_W + this cell's own 2+2 padding, declared on both so the
             cell and the area agree by arithmetic rather than by luck. */
          style={{
            width: BADGE_AREA_W + 4,
            padding: "6px 2px",
            textAlign: "center",
          }}
        >
          {edit === "assignee" ? (
            <AssigneePicker
              co={co}
              currentUser={currentUser}
              value={t.assignedCoId}
              // Phase 28 (item F) — edit-grant holders (admin / RP / tasks-edit
              // via canEditOthersTasks) may assign to ANYONE, not just self.
              canAssignAll={!!permIsAdmin || !!isRP || !!canEditOthersTasks}
              className="field-input"
              style={{ width: "100%", margin: 0, fontSize: FS.s10_5, padding: "1px 2px" }}
              autoOpen
              onChange={(v) =>
                commitField({
                  assignedCoId: v ? +v : null,
                })
              }
              onBlur={() => setEdit(null)}
            />
          ) : (
            (() => {
              const asn = co.find((x: any) => x.id === t.assignedCoId);
              // BRIEF 87 §10(ii)(b) [RULED] — a subtask assignee's badge shows on
              // that subtask AND on the parent task, BEHIND the principal
              // assignee's. Principal first, subtask assignees after, so the
              // reading order is "whose task this is, then who else is in it".
              //
              // The principal is excluded from the tail (he is already the head)
              // and the tail is deduplicated: two subtasks assigned to the same
              // person are one person on the row, not two badges.
              //
              // ARCHIVED AND DONE SUBTASKS ARE INCLUDED, deliberately. The row
              // gate already counts them — rowAssigneeUnion has ALWAYS counted
              // subtasks[].assignedCoId (alertEngine.js:313), and C65's contract
              // is that a done-subtask holder keeps his row-level access. A badge
              // that disappeared when the subtask was ticked would show less than
              // the permission system already grants, which is the worse of the
              // two errors.
              //
              // BRIEF 116 §2 [RULED] — AND THE TAIL LEAVES THE ROW. The stack
              // above grew with the row's people; Laurent's screenshot is seven
              // badges pushing the due date out of view, and the ruler agrees:
              // watt's task 205 drew 102px of badge and stretched this column to
              // 106 FOR EVERY ROW IN THE TABLE. Two badges now, no more — the
              // principal, and the viewer when a live subtask of this row is
              // his. Everyone else is named in the area's `title` below.
              // `_stIds` survives as exactly that tail: it is the tooltip's
              // list, no longer the stack's.
              type Person = { id: number };
              /* BRIEF 117 §3 — `_stIds` IS GONE with the tooltip it fed. It was
                 116 §2's tail-as-a-string: the subtask assignees minus the
                 principal, deduped, comma-joined into the area's `title`. The
                 panel lists them under their own heading now, and it keeps the
                 principal in the section above rather than subtracting him, so
                 the two sections read as roles rather than as a stack and its
                 remainder. The derivation moved to `badgeSections`. */
              /* §2 — badge 2, and the ONLY member of the old tail that still
                 draws: the viewer, when a live subtask of this row is assigned
                 to him and he is not already the principal. `liveSubs` is the
                 row's own non-archived list (:280), so the house meaning of
                 "live" is used rather than a new one. The ringed-self styling
                 is untouched — `isMe` still reaches `InitialsBadge`. */
              /* BRIEF 117 §4 — and being ON the task counts, not only holding
                 a subtask of it. The event half is where the loss was measured
                 (see badgeStack.ts); the two kinds take the one predicate so a
                 second rule cannot appear here later. `t.assigned` is the wire
                 shape's own array, of which `assignedCoId` is element 0. */
              const _viewerSub = viewerInRow(
                t.assigned,
                liveSubs,
                currentUser,
                t.assignedCoId,
              );
              const _me2 = _viewerSub
                ? (co as Person[]).find((x) => x.id === currentUser)
                : null;
              const _stAsn: Person[] = _me2 ? [_me2] : [];
              /* BRIEF 117 §3 — THE NAMES LEAVE THE `title` FOR THE PANEL, and
                 the click hint stays in it. 116 §2 put the row's people into
                 this one string because a badge could not hold them; §3 gives
                 them structure instead — sections, one line each,
                 `INITIALS — Full Name` — and a native tooltip beside a panel
                 over the same element is worse than either. What a `title` is
                 still the right shape for is the AFFORDANCE: one short sentence
                 saying the area is also a control. `_names` is now the panel's
                 input, computed by `badgeSections` (src/lib/badgeStack.ts) so
                 neither host derives it. */
              const _sections = badgeSections(
                t.assigned,
                liveSubs,
                (id: number) =>
                  (co as (Person & NameableCo)[]).find((x) => x.id === id),
                (c: NameableCo) => ({ initials: coIni(c), name: pname(c) }),
              );
              const _areaTip = inlineOk
                ? /* `tr`, not `t`: in this file `t` is the TASK (:182 aliases
                     the hook). Typescript cannot say so — the row is `any`. */
                  asn
                  ? tr("task.assignee.reassign_tip", { fallback: "Click to reassign" })
                  : tr("task.assignee.assign_tip", { fallback: "Click to assign" })
                : "";
              const _panelSections = [
                {
                  label: tr("badges.section_main_task", { fallback: "Main task" }),
                  people: _sections.main,
                },
                {
                  label: tr("badges.section_subtasks", { fallback: "Subtasks" }),
                  people: _sections.subs,
                },
              ];
              /* BRIEF 93 §1a/§1b — THE BADGES STACK, AS THE CASE LIST'S DO.
                 `matterListCfg.tsx` overlaps its responsible partners at -6 with
                 a `--bd` outline, so two people cost barely more width than one;
                 this row set them side by side, so a busier row widened the
                 whole column and pushed the timer, the counter and the title
                 right for every row in the table. Same treatment here:
                 `size={22}`, and every badge after the FIRST gets the negative
                 margin and the outline that cuts it out of the one beneath.

                 `_first` is the index this tail continues FROM, because the tail
                 is not always a tail: with no principal assignee these badges
                 ARE the stack and the first of them must not overlap the em-dash
                 in front of it. And when the archived earmark sits between the
                 principal and this tail, the stack restarts after it — the
                 earmark is a person-shaped hole, not a badge to be cut into, and
                 overlapping it would hide the mark it exists to make.

                 93 §1b — the dimming is untouched and stays on the WRAPPER,
                 where it composes with `InitialsBadge`'s own archived 0.45
                 rather than replacing it: the badge spreads `style` AFTER
                 `dimStyle`, so an opacity passed inward would erase the archived
                 signal.

                 BRIEF 94 §1c — THE `--bd` OUTLINE IS GONE. 93 put it here to
                 match the case list and inherited its fault with it: a ring in
                 the PAGE's colour, on a row that is not the page. The separation
                 is cut instead of painted (`stackCut`) so the surface shows
                 through, and `boxShadow: isMe` is left as the only ring in the
                 cell — two colleagues, neither of them you, and nothing is
                 circled.

                 BRIEF 94 §2 — THE PRINCIPAL PAINTS ON TOP, and the whole stack
                 with it: z-index DESCENDS left to right, so the task's own
                 assignee is in front of its subtask tail and the tail reads
                 left-to-right in falling prominence. Raised rather than
                 reordered, exactly as §2 asks — the DOM order stays
                 principal-first, so the reading order is unchanged and only the
                 painting moved. A subtask assignee who is the viewer still gets
                 the `--ac` ring while sitting behind: the ring says you are
                 involved, the position says how.
                 Because the stack now paints EARLIER-in-front, each badge's hole
                 is on its LEFT — the opposite side from `coAvs`, whose order §2
                 deliberately leaves alone. */
              /* BRIEF 117 §4 — THE 0.75 GOES, and the measurement is why.
                 93 §1b put it on this wrapper because these badges were a TAIL
                 — everyone else on the row, behind the principal and dimmer
                 than him. 116 §2 collapsed that tail to exactly one person,
                 the VIEWER (`_stAsn` below is `[me]` or empty), so the only
                 thing the dim still reaches is his own badge and the `--ac`
                 ring on it: measured at 0.75 in the browser, against 1 on the
                 principal slot. A self-ring rendered at three-quarter strength
                 is most of what "seems to have been lost" looks like from a
                 chair. The `stackCut` mask stays — that is structure, the front
                 badge covering the one behind it, and it is correct. */
              const _stBadge = (p2: Person, _i: number, _first: number) => (
                <span
                  key={p2.id}
                  style={{
                    display: "inline-flex",
                    marginLeft: _i + _first > 0 ? -6 : 2,
                  }}
                >
                  <InitialsBadge
                    person={p2}
                    isMe={p2.id === currentUser}
                    size={22}
                    fontSize={9}
                    /* 118 §3 — inside `BadgeHoverArea`: the panel names them. */
                    hideTitle
                    style={{
                      position: "relative",
                      // §2 — below the principal (10) and below every badge to
                      // the left of this one.
                      zIndex: Math.max(1, 9 - (_i + _first)),
                      ...(_i + _first > 0
                        ? stackCut({ size: 22, overlap: 6, side: "left" })
                        : null),
                    }}
                  />
                </span>
              );
              /* §2 — THE AREA IS THE COLUMN. One reserved width for zero, one
                 and two badges, on the wrapper both branches share, so a row's
                 people can never move the row's dates again. `justifyContent`
                 keeps 114's centring; `flexShrink: 0` keeps the reservation
                 real inside the cell's own `textAlign: center`. */
              const _area = {
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: BADGE_AREA_W,
                flexShrink: 0,
              } as const;
              if (!asn)
                return (
                  <BadgeHoverArea
                    sections={_panelSections}
                    onClick={inlineOk ? () => setEdit("assignee") : undefined}
                    style={{
                      ..._area,
                      color: "var(--tm)",
                      fontSize: FS.s9,
                      cursor: inlineOk ? "pointer" : undefined,
                    }}
                    title={_areaTip || undefined}
                  >
                    —
                    {/* §10(ii)(b) — a task with no principal can still carry
                        subtask assignees, and they are exactly the people the
                        row gate already admits.
                        §1b — no principal, so THESE are the stack: the first of
                        them starts it and does not overlap the em-dash.
                        116 §2 — and "these" is now at most the viewer. */}
                    {_stAsn.map((p2, _i) => _stBadge(p2, _i, 0))}
                  </BadgeHoverArea>
                );
              const isMe = asn.id === currentUser;
              const _earmark = isArchivedCo(asn);
              return (
                <BadgeHoverArea
                  sections={_panelSections}
                  onClick={inlineOk ? () => setEdit("assignee") : undefined}
                  style={{
                    ..._area,
                    cursor: inlineOk ? "pointer" : undefined,
                  }}
                  title={_areaTip || undefined}
                >
                  {/* 93 §1a — the head of the stack, at the case list's size.
                      94 §2 — and the top of it: raised above the tail that
                      follows it in the DOM. */}
                  <InitialsBadge
                    person={asn}
                    isMe={isMe}
                    size={22}
                    fontSize={9}
                    /* 118 §3 — inside `BadgeHoverArea`: the panel names them. */
                    hideTitle
                    style={{ position: "relative", zIndex: Z.inlineMenu }}
                  />
                  {/* 75 §3 — a task's only person, archived: the earmark. */}
                  {_earmark && (
                    <ArchivedHoleBadge size={22} fontSize={9} style={{ marginLeft: 3 }} />
                  )}
                  {/* §10(ii)(b) — behind the principal's, dimmer.
                      §1b — overlapping it, unless the earmark broke the run. */}
                  {_stAsn.map((p2, _i) => _stBadge(p2, _i, _earmark ? 0 : 1))}
                </BadgeHoverArea>
              );
            })()
          )}
        </td>
        )}
        {/* Timer — full-variant slot (reduced renders it first, above). */}
        {!reduced && tdTimer}
        {/* Subtask counter — C75: archived rows are out of the counts;
            C69: a delegatee sees the counter/pane exactly like everyone
            else (liveSubs presence drives it, not the edit grant).
            BRIEF 105 §2b/§2c — THIS FILE HAS ITS OWN COPY OF THE BADGE and it
            is not `SubtaskCounter`. §2b names two coordinates in that
            component; §2 rules the Dashboard's row IDENTICAL to this one, so
            leaving the arrow and the width here would have made the two
            surfaces disagree in the same commit that was making them agree.
            Same number, same reason, measured the same way — see
            `SubtaskCounter.tsx`'s header for the table.
            36 → 41 is 37 plus this cell's own `6px 2px`: the Dashboard's
            counter column is 37 + its wrapper's `0 2px`, so the two reserve
            the same width to the pixel. */}
        {fur("furniture.task.subtasks") && (
        <td style={{ width: 41, padding: "6px 2px", textAlign: "center" }}>
          {liveSubs.length > 0 ? (
            (() => {
              const _ts = todayISO();
              let _od = 0,
                _due = 0,
                _open = 0;
              liveSubs.forEach((st: any) => {
                if (st.done) return;
                _open++;
                if (!st.dueDate) return;
                const _d = String(st.dueDate).slice(0, 10);
                if (_d < _ts) _od++;
                else {
                  const _diff = Math.round(
                    (new Date(_d).getTime() - new Date(_ts).getTime()) /
                      (1000 * 60 * 60 * 24),
                  );
                  if (_diff <= 3) _due++;
                }
              });
              const _done = liveSubs.length - _open;
              const _color =
                _od > 0
                  ? "var(--rd)"
                  : _due > 0
                    ? "var(--am)"
                    : "var(--tm)";
              return (
                <button
                  type="button"
                  title={`${_done}/${liveSubs.length} subtasks done${
                    _od ? `, ${_od} overdue` : ""
                  }${_due ? `, ${_due} due soon` : ""} — click to ${
                    tkExpanded.has(t.id) ? "hide" : "show"
                  }`}
                  /* 195 §A — inert on an archived row: the count stays, the
                     click and its cursor go. */
                  onClick={archived ? undefined : (e) => {
                    e.stopPropagation();
                    setTkExpanded((s: any) => {
                      const n = new Set(s);
                      if (n.has(t.id)) n.delete(t.id);
                      else n.add(t.id);
                      return n;
                    });
                  }}
                  style={{
                    fontSize: FS.s9,
                    padding: "1px 5px",
                    borderRadius: RAD.r8,
                    background: tkExpanded.has(t.id)
                      ? "var(--bg2)"
                      : "var(--bi)",
                    color: _color,
                    border: `1px solid ${_color}`,
                    fontFamily: "'JetBrains Mono',monospace",
                    cursor: archived ? "default" : "pointer",
                    /* §2c — 37px, the widest counted state. */
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 37,
                  }}
                >
                  {_done}/{liveSubs.length}
                </button>
              );
            })()
          ) : subtaskCanAdd ? (
            // Empty state: no dead "—". Offer the expansion affordance so the
            // panel opens and the first subtask can be added. Gated by the
            // subtask-write permission — a viewer who can't add still sees "—".
            // BRIEF 117 §7 — and by the LOCK and the view-only regime, because
            // this button's whole promise is "click to add the first" and 116
            // §1 took the add out of the drawer it opens.
            <button
              type="button"
              title={`No subtasks yet — click to ${
                tkExpanded.has(t.id) ? "hide" : "add the first"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setTkExpanded((s: any) => {
                  const n = new Set(s);
                  if (n.has(t.id)) n.delete(t.id);
                  else n.add(t.id);
                  return n;
                });
              }}
              style={{
                fontSize: FS.s9,
                padding: "1px 5px",
                borderRadius: RAD.r8,
                background: tkExpanded.has(t.id) ? "var(--bg2)" : "var(--bi)",
                color: "var(--tm)",
                border: "1px dashed var(--br)",
                fontFamily: "'JetBrains Mono',monospace",
                cursor: "pointer",
                /* §2c — and the empty branch reserves the same. */
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 37,
              }}
            >
              +
            </button>
          ) : (
            <span style={{ color: "var(--tm)" }}>—</span>
          )}
        </td>
        )}
        {/* Level (priority) — inline editable. Omitted when reduced.
            BRIEF 118 §5 — and omitted for the WHOLE TABLE when no row on the
            page carries a level. See the `showLevel` prop. */}
        {!reduced && showLevel && fur("furniture.task.level") && (
        <td
          style={{
            width: 22,
            textAlign: "center",
            ...(edit === "priority"
              ? { overflow: "visible", whiteSpace: "normal" }
              : {}),
          }}
        >
          {edit === "priority" ? (
            <MenuSelect
              className="field-input"
              style={{ width: "auto", margin: 0, fontSize: FS.s11, padding: "1px 2px" }}
              autoFocus
              value={t.priority || ""}
              onChange={(v) => commitField({ priority: v || null })}
              onBlur={() => setEdit(null)}
              options={[
                ["", tr("common.unset_dashed", { fallback: "— Unset —" })] as const,
                ...itemLevelList.map(
                  (o: any) => [String(o.v), String(o.l ?? "")] as const,
                ),
              ]}
            />
          ) : /* BRIEF 117 §1 — AN UNSET LEVEL RENDERS NOTHING. The em-dash that
                 stood here was the only glyph on this row that meant "no data";
                 the event card's twin (CaseEventRow.tsx) drew one too, and
                 AgendaView never did. The `<td>` STAYS — it is a table column,
                 declared width 22 and measured 30 (the width is the header's own
                 sort control, not this content: the dash and the widest ladder
                 letter both render at 10px), and a row that skipped the cell
                 would shift every column after it. The CONTENT is empty.
                 THE COST, NAMED: click-to-edit went with the glyph, so an unset
                 level is now set from the task modal — the same place it is
                 created — and no longer from the row. */
          t.priority ? (
            <span
              onClick={inlineOk ? () => setEdit("priority") : undefined}
              style={{
                fontSize: FS.s11,
                fontWeight: 700,
                color: pri.c,
                cursor: inlineOk ? "pointer" : undefined,
              }}
              title={inlineOk ? "Click to change level" : undefined}
            >
              {pri.l}
            </span>
          ) : null}
        </td>
        )}
        {/* Title — inline editable name + secondary info. In the reduced
            variant the cell navigates instead (Dashboard row-click
            semantics: scroll-queue + nav, wired by the caller). C32 — this
            is the ONLY cell that shrinks/ellipsises; data-cell="title" lets
            the column CSS truncate it while leaving the fixed controls
            fully rendered. 191 (2) — never switchable: no key. */}
        <td
          data-cell="title"
          onClick={reduced && onNavTask ? () => onNavTask(t) : undefined}
          title={
            reduced && onNavTask
              ? tr("col.open_in_case_tip", { fallback: "Open in case" })
              : undefined
          }
          style={{
            width: "100%",
            fontWeight: 500,
            textDecoration:
              t.status === "completed" || isArch ? "line-through" : "none",
            color: clr || undefined,
            fontStyle: isArch ? "italic" : undefined,
            ...(reduced && onNavTask ? { cursor: "pointer" } : {}),
            // Editing: lift the global `tbody td { max-width:28ch; overflow:hidden }`
            // cap so the title editor can span the full column without in-box scroll.
            ...(edit === "title"
              ? { maxWidth: "none", overflow: "visible", whiteSpace: "normal" }
              : {}),
          }}
        >
          {edit === "title" ? (
            <input
              className="field-input"
              style={{ width: "100%", margin: 0, fontSize: FS.s13, padding: "1px 4px" }}
              autoFocus
              defaultValue={t.title || ""}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== t.title) commitField({ title: v });
                else setEdit(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const v = (e.target as any).value.trim();
                  if (v && v !== t.title) commitField({ title: v });
                  else setEdit(null);
                } else if (e.key === "Escape") {
                  setEdit(null);
                }
              }}
            />
          ) : (
            /* Round 13 (C72) put the note and the document toggle on a
               compact SECOND ROW under the title (the Events pattern).
               BRIEF 95 §3a(b)+(c) — THEY COME BACK ONTO THE TITLE LINE.
               The second row cost the task row a whole line, and it put the
               documents icon's x at the mercy of the note's length: with no
               note it sat alone at the left of an empty line, and with one it
               moved wherever the note ended. Now the cell is one flex line —
               [case ·] title · note · [icon] — where the title and the note
               each shrink and ellipsise and the icon is `flexShrink: 0`, so it
               sits IMMEDIATELY after whichever text is last and is never
               pushed to the cell's right edge.
               (c) — with the second line gone the cell is one line high, so
               `.tkRow > td { vertical-align: middle }` (App.css:1582) already
               puts every cell on the row's axis; `alignItems: "center"` here
               does the same for this cell's own contents.
               This is the same move brief 94 §3a made on the event card, which
               is the row this one is glanced beside. */
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  /* Round 17 (C101) — with a case part present the first
                     line becomes a flex pair: the CASE span shrinks and
                     ellipsises first, the task-title span keeps priority
                     (it only clips once the case part is fully collapsed —
                     the C32 title-cell convention). */
                  /* 195 §A — the case part renders wherever a host passes a
                     label, not only in the reduced column: the Archive's rows
                     name their matter here (193 §H's ruling, on the same row
                     the matter shows). The compact column still passes it
                     with `reduced`; CV/CCV/Dashboard pass none — unchanged. */
                  display: caseLabel ? "flex" : "block",
                  ...(caseLabel ? caseLineLayout : {}),
                  /* §3a(b) — a flex item now: shrink to make room for the note
                     and the icon, never grow past what the text needs. */
                  flex: "0 1 auto",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {caseLabel ? (
                  <span
                    style={{
                      flex: "0 1 auto",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "var(--tm)",
                      fontWeight: 400,
                      fontSize: FS.s11,
                      // 209 §27 — reduced: UNDER the title (the line is a column there)
                      order: reduced ? 1 : undefined,
                    }}
                    title={caseLabel}
                  >
                    {caseLabel}
                    {reduced ? null : " ·"}
                  </span>
                ) : null}
                <span
                  onClick={inlineOk ? () => setEdit("title") : undefined}
                  style={{
                    cursor: inlineOk ? "text" : undefined,
                    color: t.title ? undefined : "var(--tm)",
                    ...(caseLabel
                      ? {
                          flex: "1 0 auto",
                          maxWidth: "100%",
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }
                      : {}),
                  }}
                  title={inlineOk ? "Click to rename" : undefined}
                >
                  {t.title || (inlineOk ? "—" : "")}
                </span>
                {t.status === "waiting" && t.waitingReason && (
                  <span style={{ fontSize: FS.s9_5, color: "var(--rd)" }}>
                    {" "}
                    — {t.waitingReason}
                  </span>
                )}
                {t.status === "waiting" &&
                  delTask2 &&
                  (() => {
                    const dc2 = co.find((x: any) => x.id === delTask2.assignedCoId);
                    const ini2 = dc2 ? coIni(dc2) : "?";
                    return (
                      <span style={{ fontSize: FS.s9_5, color: "var(--rd)" }}>
                        {" "}
                        {tr("case.task.waiting_for", { who: ini2, task: delTask2.title, fallback: "— Waiting for {who} — {task}" })}
                      </span>
                    );
                  })()}
              </span>
              {/* 95 §3a(b) put the note on the title line as the first thing
                  to give up width. BRIEF 105 §3b TAKES IT OFF THE LINE
                  ALTOGETHER — "first to give up width" is precisely Laurent's
                  complaint ("notes are not visible or truncated when
                  Description is big"), and a `title=` tooltip is not reading
                  it. It is a drawer now, below, and it is editable there. */}
              {/* BRIEF 107 §2b — the documents icon, through the shared
                  control. Twelve style properties, a title key and the
                  two-state colour left this file; what it still owns is the
                  PREDICATE, which is genuinely its own. */}
              {/* BRIEF 117 §7 — `docsCanAdd`, not `docsCanTouch`.
                  `docsToggleVisible` is `hasDocs || canTouch`: the second arm
                  exists ONLY so a row with no documents still offers a way to
                  add the first, which is the subtask chip's promise in a
                  different glyph. With the add now lock-gated, that arm was
                  opening an empty drawer that refuses adds. A row that HAS
                  documents still shows the icon under any lock — reading them
                  was never gated. */}
              {!reduced && docsToggle}
              {/* §3b — "a NotebookPen icon BESIDE the documents one".
                  109 §1d — ALWAYS rendered (Laurent's rule): muted ink when
                  empty, the documents icon's filled ink when a note exists —
                  the DRAWER_ICON_HAS/EMPTY pair RowDrawerToggle already owns.
                  The 105 §3b-iii visibility gate is retired on the surfaces
                  that carry the documents icon. */}
              <NotesToggle
                open={notesOpen}
                hasNotes={hasNoteText(t.notes)} /* 118 §9 */
                onToggle={archived ? undefined : () => setNotesOpen((v) => !v)} /* 195 §A — inert */
              />
              {/* 209 §27 — reduced: the note icon first, the documents icon after it */}
              {reduced && docsToggle}
              {/* 111 §5c — the Link icon, when the task HAS links. */}
              <LinkIcon
                matterRow={matterRow}
                item={{ k: "task", id: t.id }}
              />
            </span>
          )}
          {/* 216 addendum 4 §8 (4) [RULED 79] drew the case name here, a link-btn on a second line of this cell;
              216 addendum 6 §10 (2) [RULED 81] makes it the event card's own pill on a second ROW of the card
              (`caseBelow`, below the cells). */}
          {revDone2 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginTop: 3,
              }}
            >
              <span style={{ fontSize: FS.s9, color: "var(--gn)" }}>
                {tr("task.review_done", { fallback: "✓ Review done" })}
              </span>
              <button
                className="btn"
                style={{
                  padding: "1px 6px",
                  fontSize: FS.s9,
                  background: "var(--gs)",
                  color: "var(--gn)",
                  border: "none",
                  borderRadius: RAD.r5,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  svTkData({ ...t, status: "inprogress" });
                }}
              >
                {tr("task.resume", { fallback: "Resume" })}
              </button>
            </div>
          )}
        </td>
        {/* Status — round 14 (C82a) omitted it in the reduced (pop-out/column)
            variant. 209 §27 (addendum 6) — IT COMES BACK: « the column's own
            header says its rows carry it ». In the reduced row this cell is the
            last one (due and actions stay out), and the pill works as in the
            case view — its menu opens in the pop-out's own document (StatusPill
            anchors its portal there). */}
        {fur("furniture.task.status") && (
        <td style={{ width: "1px", padding: reduced ? "6px 2px 6px 4px" : "6px 0 6px 2px", whiteSpace: "nowrap" }}>
          {/* 195 §A — the display-only variant on an archived row (the
              Agenda's readonly pill): the status is read, never set here. */}
          {TkStatusTag(t, archived ? true : undefined)}
        </td>
        )}
        {/* Due — inline editable. Omitted when reduced. */}
        {!reduced && fur("furniture.task.due") && (
        <td
          rowSpan={pillRow ? 2 : undefined} /* 216 addendum 7 §11 (2) — centred between the row and its pill row */
          style={{
            width: "1px",
            color: "var(--tp)",
            textAlign: "right",
            whiteSpace: "nowrap",
            /* BRIEF 102 §3a — THE RIGHT GAP IS THE EVENT CARD'S LEFT GAP, AND
               IT IS 10px. Measured in the browser rather than read: on an event
               card the block's left edge sits 10px inside the card
               (`.event-item { padding: 7px 9px }` plus its 1px border,
               App.css:719), and here it sat 4px from its cell's edge.
               §3's premise was that this cell took the table's generic
               `tbody td { padding: 6px 10px }` and needed no more than a
               rounding. It does not: this line has declared its own padding all
               along, so the inherited 10px never applied and the gap was 4px,
               not 10px. The number is still the one §3a asks for — taken from
               the surface that already looks right — the distance it had to
               travel was just larger than the brief expected.
               THE LEFT STAYS AT 4px: this cell is `width: 1px` between the
               status pill and the actions column, and widening its left would
               move the status pill for no reason §3 gives. */
            padding: "6px 10px 6px 4px",
            ...(edit === "date" ? { overflow: "visible" } : {}),
          }}
        >
          {edit === "date" ? (
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
            >
              {/* BRIEF 95 §3a(a) — the same one date control the drawer below
                  uses. `onChange` still hands out the browser's ISO string, so
                  the display format never reaches `svTkData`. */}
              <DateField
                wrapStyle={{ margin: 0 }}
                style={{ width: "auto", margin: 0, fontSize: FS.s12, padding: "1px 2px" }}
                autoFocus
                value={(t.dueDate || "").slice(0, 10)}
                onChange={(v) => svTkData({ ...t, dueDate: v || null })}
                onBlur={() => setEdit(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") setEdit(null);
                }}
              />
              {t.dueDate && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: "2px 3px", fontSize: FS.s9 }}
                  title={tr("modal.task.clear_due_date", {
                    fallback: "Clear due date",
                  })}
                  onClick={() => commitField({ dueDate: null })}
                >
                  <X size={10} />
                </button>
              )}
            </span>
          ) : (
            /* BRIEF 100 §2 — THE DUE DATE IS THE EVENT CARD'S DATE BLOCK.
               RULED by Laurent: day, month, year, stacked, and still on the far
               right — the column does not move, only what is inside it.
               IT REUSES THE EVENT BLOCK'S CLASSES ON PURPOSE. The brief's title
               is "one date block on both surfaces", and one definition is the
               only way two surfaces cannot drift — §1a's bold month reaches this
               block by construction, exactly as it reaches the Dashboard's. The
               `event-` prefix is historical and is now a misnomer on a task row;
               renaming it across three hosts and the stylesheet is a mechanical
               unit of its own and is named in the PR, not smuggled in here.
               PLAIN, AND DELIBERATELY SO (§2a) — REVERSED BY BRIEF 104 §3, AND
               WITHDRAWN IN PLACE RATHER THAN DELETED. The sentence that stood
               here read "the urgency signal is on the TITLE — `tkClr(t)` at :144
               is read at :882 and NOWHERE ELSE". It was reasoned and correct on
               the evidence 100 had: the block was one line among several and the
               title was the heaviest thing in the row. What changed is that the
               block BECAME the heaviest thing in the row — which 100 itself
               flagged and declined to act on — and Laurent has now seen the
               Dashboard doing both and wants both everywhere. So `clr` gains a
               third reader, three lines down, and the count in that sentence is
               no longer the point rather than no longer true.
               §2b — the edit state is untouched: clicking still opens
               `DateField` above, and the clear button with it. */
            /* BRIEF 101 §1 — `DateBlock`, and the empty state stays this
               host's own: a bare em-dash with no wrapper, which is not what
               `CaseEventRow` shows and never was. */
            /* 115 §1 — `dueShown`: t.dueDate unless the host passed the
               viewer-relative date (the Dashboard does). The inline editor
               above still edits t.dueDate — it is unreachable on the one host
               that passes viewerDue (locked), and a delegatee's subtask date
               must never be written onto the parent. */
            dueShown ? (
              <DateBlock
                date={dueShown}
                /* BRIEF 104 §3a — THE DAY CARRIES THE RED. `clr` is `tkClr(t)`
                   at :148, already computed for the title, so §5's third stop
                   could not fire: this call site does not have to reach for the
                   urgency helper, it is already holding it. `undefined` is a
                   real answer from that helper meaning "no opinion", and
                   `DateBlock` spreads nothing when it gets one — so an ordinary
                   task's day is unchanged and only a late or soon one is
                   coloured. */
                dayColor={clr}
                onClick={inlineOk ? () => setEdit("date") : undefined}
                style={inlineOk ? { cursor: "pointer" } : undefined}
                title={inlineOk ? "Click to change due date" : undefined}
              />
            ) : (
              <span
                onClick={inlineOk ? () => setEdit("date") : undefined}
                style={inlineOk ? { cursor: "pointer" } : undefined}
                title={inlineOk ? "Click to change due date" : undefined}
              >
                {"—"}
              </span>
            )
          )}
        </td>
        )}
        {/* Actions — omitted when reduced. 191 (2) — never switchable: no key. */}
        {!reduced && (
        <td rowSpan={pillRow ? 2 : undefined} style={{ whiteSpace: "nowrap", ...(archived ? { maxWidth: "none", overflow: "visible" } : {}) /* 195 §A — the slot escapes the global cell cap */ }}>
          {!locked && !isArch && canEdit && (
            <button
              className="btn btn-ghost"
              style={{ padding: "1px 4px", fontSize: FS.s9 }}
              onClick={() => openModal("tk", { ...t, _caseFixed: true })}
            >
              <Pencil size={I.s} />
            </button>
          )}
          {/* BRIEF 136 §2 [RULED] — "in Unlock mode, the archive icon should
              appear only when status is cancelled or completed (so one cannot
              archive without mentioning why)." Archiving becomes the SECOND
              step: the task is finished or dropped first, and the status that
              precedes the archive is the reason the record keeps.

              ONE GATE ON BOTH BUTTONS, and the brief asks whether that is
              right, because THEY ARE NOT THE SAME ACTION. The `isOwn` arm is a
              DELETE — `onDeleteTask` is "view-specific confirmAndUndo hard
              delete" (:128) and CaseTasksSection.tsx:668 hands it a confirm
              that filters the row out of `tk`, which the `s()` engine turns
              into a real delete with a 5s undo. Only the second arm archives
              (`status:"archived"`, which since 112 §6 `saveTask` converts into
              the Phase-28 archive at App.tsx:6928).

              They are gated together anyway, because to a reader there is ONE
              icon: same glyph, same amber, same title, same place in the row.
              Gate the archive alone and Laurent's rule would hold for
              everybody except the task's own creator, on the same row, with
              nothing on screen to explain the difference — a rule that is true
              for some viewers is worse than either answer.

              WHAT THE `isOwn` ARM LOSES is its reach on an ARCHIVED row — it
              carried no `!isArch` where the second arm did. MEASURED, that
              reach is already unreachable: `routes/tasks.js:198-204` excludes
              both `archived: true` rows and legacy `status:'archived'` rows
              from every default list, so no live row this component renders
              can be in that state. What the row can no longer do on a task
              that IS archived, ArchiveView does — it lists them and purges
              them (`purgeTask`), which is the entry point App.tsx:10458 has
              always named as the only one.

              182 addition 4 — the gate no longer HIDES the icon: every
              unlocked, non-archived row renders it, dimmed with its reason
              until the status is terminal (`archiveTitle` / `archiveDim`,
              above). `!isArch` is now explicit where `canArchive` used to
              carry it. */}
          {!locked &&
            canEdit &&
            !isArch &&
            (isOwn ? (
              <button
                className="btn"
                style={{
                  padding: "1px 4px",
                  fontSize: FS.s9,
                  marginLeft: 2,
                  background: "rgba(245,158,11,.12)",
                  color: "var(--am)",
                  border: "1px solid rgba(245,158,11,.30)",
                  borderRadius: RAD.r4,
                  ...archiveDim,
                }}
                onClick={() => {
                  if (!canArchive) return; // 182 addition 4 — dimmed, not gone
                  onDeleteTask(t);
                }}
                disabled={!canArchive}
                title={archiveTitle}
              >
                <Archive size={I.s} />
              </button>
            ) : (
              <button
                className="btn"
                style={{
                  padding: "1px 4px",
                  fontSize: FS.s9,
                  marginLeft: 2,
                  background: "rgba(245,158,11,.12)",
                  color: "var(--am)",
                  border: "1px solid rgba(245,158,11,.30)",
                  borderRadius: RAD.r4,
                  ...archiveDim,
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!canArchive) return; // 182 addition 4 — dimmed, not gone
                  svTkData({ ...t, status: "archived" });
                }}
                disabled={!canArchive}
                title={archiveTitle}
              >
                <Archive size={I.s} />
              </button>
            ))}
          {/* 195 §A — the archived row's two actions, the host's, in the
              cell the pencil and the archive button stand in on a live row. */}
          {archived ? archivedActions : null}
        </td>
        )}
      </tr>
      {/* 216 addendum 6 §10 (2) [RULED 81] — THE CASE AS THE SAME PILL AN EVENT'S CARD WEARS, AT THE SAME PLACE: a
          second row of the card, the pill at its right end, before the drawers (the Dashboard wraps each task in a
          card of its own — one table per card — so this row, the drawers below and the row above are one box).
          Laurent, 15/10: « the case name should be a "pill" like for Events (now just plain text) ». One
          definition (MatterChip.tsx): the 20-character cut, the hover with the ref, the jump; display-only where
          the matter cannot be opened (an off-team delegatee). A matter's own rows and the compact column pass no
          `caseBelow` and render as before. The cell spans the row's cells (the drawers' span plus their two
          leading cells; the reduced row's six). */}
      {below?.label && pillRow && (
        /* 216 addendum 7 §11 (2) [RULED 84] — the pill in the status pill's column (its cell's own 2 px left
           inset), the due date and the actions cell above spanning down to this row */
        <tr className="tkCaseRow" data-case-below="">
          <td colSpan={cellsBeforeStatus} style={{ padding: 0, borderBottom: "none" }} />
          <td
            data-case-pill=""
            style={{ padding: "0 0 6px 2px", textAlign: "left", maxWidth: "none", overflow: "visible", borderBottom: "none", whiteSpace: "nowrap" }}
          >
            <MatterChip chip={{ label: below.label, title: below.title ?? below.label, onClick: below.onClick }} />
          </td>
        </tr>
      )}
      {below?.label && !pillRow && (
        <tr className="tkCaseRow" data-case-below="">
          <td
            colSpan={reduced ? 6 : drawerSpan + 2}
            style={{ padding: "0 8px 6px", textAlign: "right", maxWidth: "none", overflow: "visible", borderBottom: "none" }}
          >
            <MatterChip chip={{ label: below.label, title: below.title ?? below.label, onClick: below.onClick }} />
          </td>
        </tr>
      )}
      {tkExpanded.has(t.id) && (
        <tr className="subtaskDrawer">
          {/* BRIEF 114 §1 — THE DRAWER STARTS AT THE ASSIGNEE COLUMN, AND THE
              TABLE IS WHAT PUTS IT THERE.
              The glance: a Dashboard subtask's initials badge sits on the same
              vertical line as its row's; in CV/CCV it does not. MEASURED on
              watt before changing anything (1600px viewport): the row's
              assignee cell begins 76px into the row (TYPE 60 + Eisenhower 16)
              and its own `6px 2px` puts the principal badge at +78; the drawer
              began at +40, so the two columns stood 37px apart.
              A PIXEL INDENT CANNOT CLOSE THAT, and the measurement is why:
              those 76px are a CONTENT-SIZED auto-layout column pair (the TYPE
              chip's own width — "ADMIN" measured 60 where the declared width is
              38 — and an Eisenhower cell the browser squeezed from 22 to 16),
              so a `padding-left: 77px` would be right for this firm's type
              codes at this window width and drift for any other. The table
              already knows the number: two empty leading cells hand the drawer
              the assignee column's own left edge, at any type width, any
              viewport, any zoom. The `2px` left padding is that cell's own
              (`:599` — `padding: "6px 2px"`), so drawer content and column
              content share one edge.
              The REDUCED (compact-column) variant is untouched and keeps its
              single cell: it renders NO assignee column at all (`:598`), so
              there is nothing there to meet, and C83ii's 8px reclaim stands.
              The EVENT drawer is untouched for the same measured reason,
              inverted: an event card has no badge COLUMN — its badges follow a
              variable-width type chip (offsets 90/82/68 for INTRO/CCL/? inside
              one case) and each host orders the card differently (Dashboard
              133, Agenda 217-283), so no single `.evSubDrawer` indent can meet
              anything, and per-host ones are exactly the scatter 112 §2
              refused. */}
          {!reduced && <td colSpan={2} />}
          <td
            /* BRIEF 117 §2b — 8 → 6, AND THE TABLE IS WHAT SUPPLIES THE PILL'S
               x. §2b asks the subtask pill to sit at its master's pill x in the
               LOCKED state. MEASURED FIRST, and the measurement refuses a pixel
               reservation outright: EVERY column from the status pill rightwards
               is `width: "1px"` — shrink-to-fit (status :1247, due :1253, the
               actions cell). Their widths are the firm's own status labels and
               its date format, so the only fixed x on this side of the table is
               the table's own right edge. That is 114 §1's ruling restated for
               the right-hand side — a content-sized column cannot be met by a
               pixel indent, so the TABLE supplies it — and this colSpan plus the
               empty trailing cell below is the whole of how.
               The drawer's content cell now ENDS on the status/due column
               boundary, so the pill (the last item in every subtask row) lands
               its right edge exactly there, at any window width and under any
               status vocabulary. Cost, measured at 1600: the cell loses the due
               + actions columns (60) and gains back its own 40px right inset,
               which the pill would otherwise sit inside — net 20px narrower than
               the docs and notes drawers beside it.
               `reduced` spans 6 since 209 §27: the status pill is back at the
               end of the reduced row, and the drawer runs under the whole of it. */
            /* 118 §5 — one column fewer when the level column is not there,
               so the cell still ENDS on the status/due boundary 117 §2b put it
               on. 2 leading + 5 = 7 of 9; 2 leading + 6 = 8 of 10. */
            colSpan={reduced || showLevel ? 6 : 5}
            className={reduced ? "colSubCell" : undefined}
            /* BRIEF 93 §3a/§3b — 18 -> 40, one badge width, and ONLY here.
               §3b forbade a blind +22 and demanded the rendered result. MEASURED
               in a real browser, on a real case, before and after: the task
               drawer's content began 8px right of its own row's content and the
               EVENT drawer's began 31px right of its card's — a badge and more.
               So the event drawer was already indented and this one was not,
               which is exactly the case §3b names for changing only this side.
               After: 30px against the event's 31px, the 1px being the event
               card's own border. Both drawers now carry `padding-left: 40px`,
               reached from two different numbers because the two parents' chrome
               is not the same thing.
               114 §1 — THE FULL VARIANT'S 40px LEFT IS WITHDRAWN in place: the
               indent is now the assignee column itself (the empty cell above),
               and this declaration keeps that column's own 2px so the two share
               an edge. The RIGHT 40 is untouched, and so is the `reduced` arm —
               C83ii strips controls there to reclaim width in a 330px column,
               and 22px of indent would hand a tenth of it back.
               BRIEF 117 §2b — THE RIGHT 40 GOES, in this drawer only. It is what
               stood between the row's last item and the column boundary the
               colSpan above just moved this cell onto; keeping it would have put
               the pill 40px inside the master's status column and called it
               aligned. The docs and notes drawers keep theirs — they have no
               column to meet. */
            style={{ padding: reduced ? "4px 8px 10px" : "4px 0 10px 2px" }}
          >
            <SubtaskChecklist
              parentTaskId={t.id}
              initialSubtasks={t.subtasks || []}
              locked={locked}
              /* 115 §1 — the Dashboard's view-only regime (110 §1b), through
                 the prop the checklist has carried since 110. canWrite stays
                 the rights value so the LOOK (timer column gating) matches. */
              readOnly={readOnly}
              /* C69 — full edit follows the real grant; a delegatee gets the
                 pane read-only with HIS row tickable/timeable (per-row rule
                 inside the checklist). C70 — the pop-out is at full parity
                 now: assignee control + add row included (design reversal,
                 sanctioned). */
              canWrite={subtaskFullEdit}
              /* Round 14 (C83ii) — pop-out/column rows drop the reorder
                 chevrons + archive toggle (those live in CV/CCV/Dashboard
                 panes only); everything else keeps full C81 parity. */
              hideRowActions={reduced}
              co={co}
              currentUser={currentUser}
              assignable
              canAssignAll={!!permIsAdmin || !!isRP || !!canEditOthersTasks}
              /* C61 — done-tick hands off to the host's complete-style
                 prompt (time-log offer). */
              onCheckDone={
                typeof onSubtaskDone === "function"
                  ? (st: any) => onSubtaskDone(t, st)
                  : undefined
              }
              onItemsChange={(arr: any) =>
                sTk((prev: any) =>
                  prev.map((x: any) =>
                    x.id === t.id ? { ...x, subtasks: arr } : x,
                  ),
                )
              }
              {...timing}
            />
          </td>
          {/* BRIEF 117 §2b — the due and actions columns, empty. The cell above
              stops at the status column's right edge; these two keep the row's
              cell count at the table's and are what make that boundary real. */}
          {!reduced && <td colSpan={2} />}
        </tr>
      )}
      {/* Round 13 (C72) — documents subpane, modeled on the subtask pane.
          Toggled by the title-line File icon; rights per docsCanTouch
          (any assignee, explicit or delegation-induced, + edit grants). */}
      {docsOpen && (
        <tr className="subtaskDrawer">
          {/* 114 §1 — the same two leading cells, for the reason the subtask
              drawer above gives: a row's drawers share ONE edge (112 §1a), so
              the indent that finds the assignee column has to find it for all
              three or the drawer stack would step. */}
          {!reduced && <td colSpan={2} />}
          <td
            colSpan={drawerSpan} /* 118 §5; 209 §27 — the reduced row's 6 */
            className={reduced ? "colSubCell" : undefined}
            /* BRIEF 93 §3a/§3b — 18 -> 40, one badge width, and ONLY here.
               §3b forbade a blind +22 and demanded the rendered result. MEASURED
               in a real browser, on a real case, before and after: the task
               drawer's content began 8px right of its own row's content and the
               EVENT drawer's began 31px right of its card's — a badge and more.
               So the event drawer was already indented and this one was not,
               which is exactly the case §3b names for changing only this side.
               After: 30px against the event's 31px, the 1px being the event
               card's own border. Both drawers now carry `padding-left: 40px`,
               reached from two different numbers because the two parents' chrome
               is not the same thing.
               114 §1 — THE FULL VARIANT'S 40px LEFT IS WITHDRAWN in place: the
               indent is now the assignee column itself (the empty cell above),
               and this declaration keeps that column's own 2px so the two share
               an edge. The RIGHT 40 is untouched, and so is the `reduced` arm —
               C83ii strips controls there to reclaim width in a 330px column,
               and 22px of indent would hand a tenth of it back. */
            style={{ padding: reduced ? "4px 8px 10px" : "4px 40px 10px 2px" }}
          >
            <TaskDocsPane
              taskId={t.id}
              initialDocs={Array.isArray(t.docs) ? t.docs : undefined}
              /* 115 §1 — quiet under the Dashboard regime: chips open links,
                 nothing writes (110 §1b). */
              canWrite={readOnly ? false : docsCanTouch}
              locked={locked}
              onDocsChange={(arr) =>
                sTk((prev: any) =>
                  prev.map((x: any) => (x.id === t.id ? { ...x, docs: arr } : x)),
                )
              }
              /* 110 §1a — the stage drawer's binding (98 §4's oneDriveMatter,
                 reduced to the {caseId|ccId} the picker needs); the pane owns
                 the button and the write. Same gate as the retired slot. */
              oneDrive={
                !reduced && docsCanTouch && oneDriveMatter
                  ? oneDriveMatter.kind === "cc"
                    ? { ccId: oneDriveMatter.matter.id }
                    : { caseId: oneDriveMatter.matter.id }
                  : undefined
              }
              /* 151C §6 — the matter's stages ride the same binding (`matter`
                 IS the case/cc row, itemLinks included), and there is NO PEN
                 here any more: the pane owns the purple-link reconcile
                 (lib/stageItemLink) and the stageId write is retired from
                 this flow — the row's belongs-to stage stays the task
                 modal's own StageLinkField. Dashboard/compact mounts pass no
                 oneDriveMatter and stay link-less, exactly as they stay
                 picker-less. */
              stageLink={
                oneDriveMatter && Array.isArray(oneDriveMatter.matter?.stages)
                  ? {
                      stages: oneDriveMatter.matter.stages,
                      matterKind: oneDriveMatter.kind === "cc" ? ("cc" as const) : ("ca" as const),
                      matterId: oneDriveMatter.matter.id,
                      matterRow: oneDriveMatter.matter,
                    }
                  : undefined
              }
            />
          </td>
        </tr>
      )}
      {/* BRIEF 105 §3b — THE THIRD DRAWER, LAST, and the order is the same one
          98 §2 ruled for the first two: subtasks, then documents, then this.
          §3b-i asked what the join rules do with a third. ON THE TASK SIDE
          THERE ARE NO JOIN RULES TO RE-CHECK — a task's drawers are `<tr>`s
          inside the same `<table>` as the row, so the table's own geometry
          joins them and always has, for any number of them (91 §3b says so in
          App.css: "a task's drawer is a `<tr>` inside the same table and the
          table's own geometry joins them"). It is the EVENT card that needed
          two `:has()` rules to reproduce this, and that is where §3b-i's
          question is actually answered — see `CaseEventRow.tsx`. */}
      {notesOpen && (
        <tr className="subtaskDrawer">
          {/* 114 §1 — and the third, same edge. */}
          {!reduced && <td colSpan={2} />}
          <td
            colSpan={drawerSpan} /* 118 §5; 209 §27 — the reduced row's 6 */
            className={reduced ? "colSubCell" : undefined}
            /* 93 §3a/§3b's indent, 114 §1's edge — for the same reason the two
               drawers above carry them: one rule for all three. */
            style={{ padding: reduced ? "4px 8px 10px" : "4px 40px 10px 2px" }}
          >
            <NotesDrawer
              value={t.notes}
              /* 109 §1d — rights without the lock: the note is editable on a
                 LOCKED row; only structure follows the padlock.
                 115 §1 — which is exactly why the Dashboard regime is its own
                 prop: notes quiet there (110 §1b), and no lock can say so. */
              canWrite={readOnly ? false : notesOk}
              /* §3b-ii — `commitField` is `svTkData` plus closing the editor,
                 which is the tk modal's own save. No second write path. */
              onSave={(next) => svTkData({ ...t, notes: next })}
            />
          </td>
        </tr>
      )}
    </Fragment>
  );
}
